import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  MagnifyingGlass,
  Users,
  CheckCircle,
  Clock,
  Warning,
  DownloadSimple,
  LinkSimple,
  Copy,
  ArrowsClockwise,
  Info,
} from '@phosphor-icons/react';

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});
API.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('pa_access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const StatCard = ({ label, value, icon: Icon, color, testId }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm`} data-testid={testId}>
    <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
      <Icon size={20} weight="bold" className="text-white" />
    </div>
    <div>
      <div className="text-xs text-slate-500 font-semibold uppercase">{label}</div>
      <div className="text-2xl font-black text-slate-900">{value}</div>
    </div>
  </div>
);

const AdminMigration = () => {
  const { language } = useAuth();
  const L = (en, de) => (language === 'de' ? de : en);

  const [stats, setStats] = useState({ total: 0, activated: 0, pending: 0, no_email: 0 });
  const [tab, setTab] = useState('pending'); // all | pending | activated | no_email
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(0);       // 0-indexed
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [regenerate, setRegenerate] = useState(false);
  const [busy, setBusy] = useState(false);

  const PAGE_SIZE = 100;
  const abortRef = useRef(null);

  const loadStats = async () => {
    try {
      const { data } = await API.get('/admin/migration/stats');
      setStats(data);
    } catch (e) {
      toast.error(L('Failed to load stats', 'Statistik konnte nicht geladen werden'));
    }
  };

  const loadRows = async () => {
    // Cancel any in-flight request so a slow older tab-fetch cannot overwrite
    // the current one (fixes "Activated tab suddenly showing all users").
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const { data } = await API.get('/admin/migration/candidates', {
        params: {
          status: tab,
          search: debouncedSearch || undefined,
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
        },
        signal: controller.signal,
      });
      // Only apply if we're still the freshest request
      if (!controller.signal.aborted) {
        setRows(data.items || []);
        setRowsTotal(data.total || 0);
        setSelected(new Set());
      }
    } catch (e) {
      if (e.name !== 'CanceledError' && e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') {
        toast.error(L('Failed to load users', 'Benutzer konnten nicht geladen werden'));
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  // Debounce search input (300ms) — spare the DB from typing spam.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  // Reset page when tab or search changes.
  useEffect(() => { setPage(0); }, [tab, debouncedSearch]);
  // Fetch whenever inputs change.
  useEffect(() => { loadRows(); }, [tab, debouncedSearch, page]);

  // Search now happens server-side (see loadRows); this is just an alias.
  const filtered = rows;

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.id)));
  };

  const generateLinks = async () => {
    setBusy(true);
    try {
      const payload = {
        regenerate,
        user_ids: selected.size > 0 ? Array.from(selected) : null,
      };
      const { data } = await API.post('/admin/migration/generate-links', payload);
      toast.success(L(`Generated ${data.count} activation link(s)`, `${data.count} Aktivierungs-Link(s) erstellt`));
      await loadRows();
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed to generate links', 'Links konnten nicht erstellt werden'));
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async (onlyPending = true) => {
    try {
      const res = await API.get('/admin/migration/export.csv', {
        params: { only_pending: onlyPending },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `padelarena_activation_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(L('CSV downloaded', 'CSV heruntergeladen'));
    } catch (e) {
      toast.error(L('Failed to download CSV', 'CSV-Download fehlgeschlagen'));
    }
  };

  const copyLink = (row) => {
    if (!row.activation_token) {
      toast.info(L('No link yet — click "Generate links" first', 'Noch kein Link — bitte zuerst "Links generieren" klicken'));
      return;
    }
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
    const url = `${base}/activate?token=${row.activation_token}`;
    navigator.clipboard.writeText(url);
    toast.success(L('Link copied', 'Link kopiert'));
  };

  const statusBadge = (r) => {
    if (r.status === 'activated') {
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`row-status-${r.id}`}>{L('Activated', 'Aktiviert')}</Badge>;
    }
    if (!r.email) {
      return <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200" data-testid={`row-status-${r.id}`}>{L('No email', 'Keine E-Mail')}</Badge>;
    }
    if (r.activation_token) {
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100" data-testid={`row-status-${r.id}`}>{L('Link ready', 'Link bereit')}</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`row-status-${r.id}`}>{L('Pending', 'Ausstehend')}</Badge>;
  };

  const tabs = [
    { key: 'pending', label: L('Pending', 'Ausstehend'), count: stats.pending },
    { key: 'activated', label: L('Activated', 'Aktiviert'), count: stats.activated },
    { key: 'no_email', label: L('No email', 'Keine E-Mail'), count: stats.no_email },
    { key: 'all', label: L('All', 'Alle'), count: stats.total },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="admin-migration-page">
      <div>
        <h1 className="text-3xl font-black text-slate-900">{L('Playtomic Migration', 'Playtomic-Migration')}</h1>
        <p className="text-slate-600 text-sm mt-1">
          {L(
            'Generate activation links for imported customers and export them as CSV to send through your own mailer.',
            'Erstelle Aktivierungs-Links für importierte Kunden und exportiere sie als CSV für den Versand über deinen Mailer.'
          )}
        </p>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={L('Total', 'Gesamt')} value={stats.total} icon={Users} color="bg-slate-700" testId="stat-total" />
        <StatCard label={L('Activated', 'Aktiviert')} value={stats.activated} icon={CheckCircle} color="bg-emerald-600" testId="stat-activated" />
        <StatCard label={L('Pending', 'Ausstehend')} value={stats.pending} icon={Clock} color="bg-amber-500" testId="stat-pending" />
        <StatCard label={L('No email', 'Keine E-Mail')} value={stats.no_email} icon={Warning} color="bg-slate-400" testId="stat-no-email" />
      </div>

      {/* Action bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={generateLinks}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="generate-links-btn"
          >
            <LinkSimple size={16} className="mr-1.5" />
            {selected.size > 0
              ? L(`Generate ${selected.size} link(s)`, `${selected.size} Link(s) generieren`)
              : L('Generate links for all pending', 'Links für alle Ausstehenden generieren')}
          </Button>
          <Button
            onClick={() => downloadCsv(true)}
            variant="outline"
            data-testid="export-csv-pending-btn"
          >
            <DownloadSimple size={16} className="mr-1.5" />
            {L('Export pending as CSV', 'Ausstehende als CSV')}
          </Button>
          <Button
            onClick={() => downloadCsv(false)}
            variant="outline"
            data-testid="export-csv-all-btn"
          >
            <DownloadSimple size={16} className="mr-1.5" />
            {L('Export all as CSV', 'Alle als CSV')}
          </Button>
          <label className="flex items-center gap-2 text-sm text-slate-700 ml-auto cursor-pointer" data-testid="regenerate-toggle">
            <Checkbox checked={regenerate} onCheckedChange={(v) => setRegenerate(!!v)} />
            <ArrowsClockwise size={16} />
            {L('Regenerate existing tokens', 'Bestehende Tokens neu generieren')}
          </label>
        </div>
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex gap-2 text-sm text-slate-700">
          <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
          <div>
            {L(
              'Tokens are valid for 180 days. Each user gets a unique link like ',
              'Tokens sind 180 Tage gültig. Jeder Kunde erhält einen einzigartigen Link wie '
            )}
            <code className="bg-white border border-blue-200 rounded px-1 py-0.5 text-xs">/activate?token=…</code>.
            {L(' Distribute via your Playtomic mailer.', ' Versand über deinen Playtomic-Mailer.')}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
              tab === t.key
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label} <span className="opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={L('Search name, email, phone…', 'Name, E-Mail, Telefon suchen…')}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onCheckedChange={toggleAll}
                  data-testid="select-all"
                />
              </TableHead>
              <TableHead>{L('Name', 'Name')}</TableHead>
              <TableHead>{L('Email', 'E-Mail')}</TableHead>
              <TableHead>{L('Phone', 'Telefon')}</TableHead>
              <TableHead>{L('Status', 'Status')}</TableHead>
              <TableHead className="text-right">{L('Actions', 'Aktionen')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                  {L('Loading…', 'Wird geladen…')}
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-400 py-8" data-testid="empty-state">
                  {L('No users found', 'Keine Benutzer gefunden')}
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((r) => (
              <TableRow key={r.id} data-testid={`row-${r.id}`}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(r.id)}
                    onCheckedChange={() => toggleOne(r.id)}
                    data-testid={`select-${r.id}`}
                  />
                </TableCell>
                <TableCell className="font-semibold text-slate-900">{r.name || '—'}</TableCell>
                <TableCell className="text-slate-700 font-mono text-xs">{r.email || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-slate-600 text-sm">{r.phone || '—'}</TableCell>
                <TableCell>{statusBadge(r)}</TableCell>
                <TableCell className="text-right">
                  {r.activation_token && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyLink(r)}
                      data-testid={`copy-link-${r.id}`}
                    >
                      <Copy size={14} className="mr-1" />
                      {L('Copy link', 'Link kopieren')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {rowsTotal > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-slate-600" data-testid="pagination-bar">
          <div>
            {L('Showing', 'Zeige')}{' '}
            <span className="font-bold text-slate-900">{page * PAGE_SIZE + 1}</span>
            {' – '}
            <span className="font-bold text-slate-900">{Math.min((page + 1) * PAGE_SIZE, rowsTotal)}</span>
            {' '}{L('of', 'von')}{' '}
            <span className="font-bold text-slate-900">{rowsTotal}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0 || loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="pagination-prev"
            >
              {L('Previous', 'Zurück')}
            </Button>
            <span className="px-2 font-mono text-xs text-slate-500">
              {page + 1} / {Math.max(1, Math.ceil(rowsTotal / PAGE_SIZE))}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={(page + 1) * PAGE_SIZE >= rowsTotal || loading}
              onClick={() => setPage((p) => p + 1)}
              data-testid="pagination-next"
            >
              {L('Next', 'Weiter')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMigration;
